'use client';

import { defaultPerPageOptions, getTablePerPageState, setTablePerPageState } from 'shared/base';

import {
  useReactRouterSyncWithUrlParam,
  useReactRouterUpdateSearchParams,
} from './useReactRouterSyncWithUrlParam';

/**
 * useReactRouterPagination is a React hook that is depending on react-router which returns
 * pagination values synchronised with the URL.
 */
export function useReactRouterPagination({
  defaultPerPage = defaultPerPageOptions[0],
  pageParameter = 'page',
  perPageOptions = defaultPerPageOptions,
  perPageParameter = 'per_page',
  tableId,
}: {
  defaultPerPage?: number;
  pageParameter?: string;
  perPageOptions?: number[];
  perPageParameter?: string;
  tableId: string;
}) {
  const [page, setPage] = useReactRouterSyncWithUrlParam<number>({
    defaultValue: 1,
    getInitialValue: handlePageValue,
    paramKey: pageParameter,
    parseParamValue: handlePageValue,
  });

  const [perPage, setPerPage] = useReactRouterSyncWithUrlParam<number>({
    defaultValue: defaultPerPage,
    getInitialValue: (paramValue) => {
      const paramValueNumber = Number.parseInt(paramValue, 10);
      if (perPageOptions.includes(paramValueNumber)) {
        return paramValueNumber;
      }

      const perPageState = getTablePerPageState(tableId);
      if (perPageState && perPageOptions.includes(perPageState)) {
        return perPageState;
      }

      return defaultPerPage;
    },
    paramKey: perPageParameter,
    parseParamValue: (paramValue) => {
      const paramValueNumber = Number.parseInt(paramValue, 10);
      if (perPageOptions.includes(paramValueNumber)) {
        return paramValueNumber;
      }

      return defaultPerPage;
    },
  });

  const updateSearchParams = useReactRouterUpdateSearchParams();

  const handleSetPerPage = (perPage: number) => {
    updateSearchParams({
      ...setPage(1, true),
      ...setPerPage(perPage, true),
    });

    setTablePerPageState(tableId, perPage);
  };

  return {
    apiPage: page,
    perPage,
    setAPIPage: setPage,
    setPerPage: handleSetPerPage,
    setTablePage: (tablePage: number, updateStateOnly?: boolean) => {
      return setPage(tablePage + 1, updateStateOnly);
    },
    tablePage: page - 1,
  };
}

function handlePageValue(paramValue: string) {
  const paramValueNumber = Number.parseInt(paramValue, 10);
  if (!Number.isNaN(paramValueNumber) && paramValueNumber > 0) {
    return paramValueNumber;
  }

  return 1;
}
